"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.default = ProfilePersonalInfoCard;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = __importDefault(require("react"));
const react_native_1 = require("react-native");
const react_native_fontawesome_1 = require("@fortawesome/react-native-fontawesome");
const cancelable_promise_1 = require("cancelable-promise");
const Theme_1 = require("../Theme");
const ThemeContext_1 = require("../ThemeContext");
const MyProfile_1 = __importDefault(require("../api/MyProfile"));
const Card_1 = __importDefault(require("../components/Card"));
const CardTitle_1 = __importDefault(require("../components/CardTitle"));
const Field_1 = __importDefault(require("../components/Field"));
const Loading_1 = __importDefault(require("../components/Loading"));
const Button_1 = __importDefault(require("../components/Button"));
const UserContext_1 = require("../contexts/UserContext");
const react_i18next_1 = require("react-i18next");
const FadeInView_1 = __importDefault(require("../components/FadeInView"));
function ProfilePersonalInfoCard(props) {
    const { t } = (0, react_i18next_1.useTranslation)();
    const THEME = (0, ThemeContext_1.useTheme)();
    const userContext = react_1.default.useContext(UserContext_1.UserContext);
    const promise = react_1.default.useRef(null);
    const [status, setStatus] = react_1.default.useState(null);
    const editable = props.editable ?? true;
    const [personalInfo, setPersonalInfo] = react_1.default.useState({
        lastName: "",
        firstName: "",
        email: "",
        phone: "",
        addressLine1: "",
        addressLine2: "",
        city: "",
        stateAbbr: "",
        zipCode: "",
        county: "",
        mailingAddressLine1: "",
        mailingAddressLine2: "",
        mailingCity: "",
        mailingStateAbbr: "",
        mailingZipCode: "",
        mailingCounty: ""
    });
    const refresh = react_1.default.useCallback(() => {
        promise.current?.cancel();
        setStatus("loading");
        promise.current = (0, cancelable_promise_1.cancelable)(userContext.getToken().then((token) => MyProfile_1.default.getPersonalInfo(token, props.employeeId))).then((data) => {
            setPersonalInfo(data);
            setStatus("done");
        }).catch((e) => {
            setPersonalInfo(null);
            setStatus("error");
        });
    }, []);
    const editInfo = () => {
        props.navigation.navigate("PersonalInfoEdit");
        react_native_1.DeviceEventEmitter.addListener("event.personalInfoUpdate", () => refresh());
    };
    react_1.default.useEffect(() => {
        refresh();
        return () => {
            promise.current?.cancel();
            react_native_1.DeviceEventEmitter.removeAllListeners("event.personalInfoUpdate");
        };
    }, []);
    const formatMobileNumber = (text) => {
        var cleaned = ("" + text).replace(/\D/g, "");
        var match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
        if (match) {
            //prettier-ignore
            var intlCode = (match[1] ? '+1 ' : ''), number = [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('');
            return number;
        }
        return text;
    };
    const has = (v) => !!(v && v.trim());
    const formatAddress = (a) => {
        const street = [a.line1, a.line2].filter(has).join(" ").trim();
        const stateZip = [a.state, a.zip].filter(has).join(" ").trim();
        const cityStateZip = has(a.city) ? `${a.city}, ${stateZip}`.trim() : stateZip;
        const countyPart = a.includeCountyLabel && has(a.county)
            ? `${a.county} County`
            : has(a.county)
                ? `${a.county}`
                : "";
        return [street, cityStateZip, countyPart].filter(has).join(" ").replace(/\s+/g, " ").trim();
    };
    const homeAddressText = formatAddress({
        line1: personalInfo.addressLine1,
        line2: personalInfo.addressLine2,
        city: personalInfo.city,
        state: personalInfo.stateAbbr,
        zip: personalInfo.zipCode,
        county: personalInfo.county,
        includeCountyLabel: true,
    });
    const effectiveMailingText = has(personalInfo.mailingAddressLine1)
        ? formatAddress({
            line1: personalInfo.mailingAddressLine1,
            line2: personalInfo.mailingAddressLine2,
            city: personalInfo.mailingCity,
            state: personalInfo.mailingStateAbbr,
            zip: personalInfo.mailingZipCode,
            county: personalInfo.mailingCounty,
            includeCountyLabel: has(personalInfo.mailingCounty),
        })
        : homeAddressText;
    const w2AddressText = has(personalInfo.w2AddressLine1)
        ? formatAddress({
            line1: personalInfo.w2AddressLine1,
            line2: personalInfo.w2AddressLine2,
            city: personalInfo.w2City,
            state: personalInfo.w2StateAbbr,
            zip: personalInfo.w2ZipCode,
            county: personalInfo.w2County,
            includeCountyLabel: has(personalInfo.w2County),
        })
        : effectiveMailingText;
    return ((0, jsx_runtime_1.jsxs)(Card_1.default, { children: [(0, jsx_runtime_1.jsx)(CardTitle_1.default, { title: t('profile:text.persInfo'), children: editable &&
                    (0, jsx_runtime_1.jsxs)(Button_1.default, { type: "outline", disabled: userContext.isImpersonated, accessibilityHint: t('profile:hints.editInfo'), onPress: () => editInfo(), children: [(0, jsx_runtime_1.jsx)(react_native_1.Text, { style: { color: userContext.isImpersonated ? THEME.TextDisabled : THEME.Primary, fontSize: 14, fontWeight: '500', marginHorizontal: 6 }, children: t('common:buttons.edit') }), (0, jsx_runtime_1.jsx)(react_native_fontawesome_1.FontAwesomeIcon, { color: userContext.isImpersonated ? THEME.TextDisabled : THEME.Primary, icon: Theme_1.ICONS.Regular.Pen, size: 14, style: { marginRight: 6 } })] }) }), (status == "done" && ((0, jsx_runtime_1.jsxs)(FadeInView_1.default, { style: { marginVertical: 0, marginHorizontal: 0, marginTop: 4 }, children: [(0, jsx_runtime_1.jsxs)(react_native_1.View, { style: { flexDirection: 'row' }, children: [(0, jsx_runtime_1.jsx)(react_native_fontawesome_1.FontAwesomeIcon, { color: THEME.Primary, icon: Theme_1.ICONS.Regular.User, size: 18, style: { marginRight: 12, alignSelf: 'center' } }), (0, jsx_runtime_1.jsx)(react_native_1.View, { style: { flex: 1 }, children: (0, jsx_runtime_1.jsx)(Field_1.default, { caption: t('profile:labels.name'), value: personalInfo.firstName + " " + personalInfo.lastName }) })] }), (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: { flexDirection: 'row' }, children: [(0, jsx_runtime_1.jsx)(react_native_fontawesome_1.FontAwesomeIcon, { color: THEME.Primary, icon: Theme_1.ICONS.Regular.Envelope, size: 18, style: { marginRight: 12, alignSelf: 'center' } }), (0, jsx_runtime_1.jsx)(react_native_1.View, { style: { flex: 1 }, children: (0, jsx_runtime_1.jsx)(Field_1.default, { caption: t('profile:labels.email'), value: !!personalInfo.email ? personalInfo.email : "—" }) })] }), (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: { flexDirection: 'row' }, children: [(0, jsx_runtime_1.jsx)(react_native_fontawesome_1.FontAwesomeIcon, { color: THEME.Primary, icon: Theme_1.ICONS.Regular.Phone, size: 18, style: { marginRight: 12, alignSelf: 'center' } }), (0, jsx_runtime_1.jsx)(react_native_1.View, { style: { flex: 1 }, children: (0, jsx_runtime_1.jsx)(Field_1.default, { caption: t('profile:labels.phone'), value: !!formatMobileNumber(personalInfo.phone) ? formatMobileNumber(personalInfo.phone) : "—" }) })] }), (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: { flexDirection: 'row' }, children: [(0, jsx_runtime_1.jsx)(react_native_fontawesome_1.FontAwesomeIcon, { color: THEME.Primary, icon: Theme_1.ICONS.Regular.LocationDot, size: 18, style: { marginRight: 12, alignSelf: 'center' } }), (0, jsx_runtime_1.jsx)(react_native_1.View, { style: { flex: 1 }, children: (0, jsx_runtime_1.jsx)(Field_1.default, { caption: t('profile:labels.address'), value: `${personalInfo.addressLine1} ${personalInfo.addressLine2} ${personalInfo.city}, ${personalInfo.stateAbbr} ${personalInfo.zipCode} ${personalInfo.county} County`.replace(/\s+/g, ' ') }) })] }), !!personalInfo.mailingAddressLine1 &&
                        (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: { flexDirection: 'row' }, children: [(0, jsx_runtime_1.jsx)(react_native_fontawesome_1.FontAwesomeIcon, { color: THEME.Primary, icon: Theme_1.ICONS.Regular.LocationDot, size: 18, style: { marginRight: 12, alignSelf: 'center' } }), (0, jsx_runtime_1.jsx)(react_native_1.View, { style: { flex: 1 }, children: (0, jsx_runtime_1.jsx)(Field_1.default, { caption: t('profile:labels.mailAddress'), value: `${personalInfo.mailingAddressLine1} ${personalInfo.mailingAddressLine2} ${personalInfo.mailingCity}, ${personalInfo.mailingStateAbbr} ${personalInfo.mailingZipCode} ${personalInfo.mailingCounty ?? ""} ${personalInfo.mailingCounty != null ? "County" : ""}`.replace(/\s+/g, ' ') }) })] }), (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: { flexDirection: 'row' }, children: [(0, jsx_runtime_1.jsx)(react_native_fontawesome_1.FontAwesomeIcon, { color: THEME.Primary, icon: Theme_1.ICONS.Regular.LocationDot, size: 18, style: { marginRight: 12, alignSelf: 'center' } }), (0, jsx_runtime_1.jsx)(react_native_1.View, { style: { flex: 1 }, children: (0, jsx_runtime_1.jsx)(Field_1.default, { caption: t('profile:labels.w2Address'), value: w2AddressText || "—" }) })] })] }))) || (0, jsx_runtime_1.jsx)(Loading_1.default, { retryable: true, loading: status == "loading", error: status == "error", onRetry: () => refresh() })] }));
}
